import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import API from "../services/api";
import "../pages/telesales.css";

import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from "recharts";

const COLORS = ["#4f46e5", "#f59e0b"];

function TelesalesDashboard() {

  const { id } = useParams();

  const [calls, setCalls] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [deals, setDeals] = useState([]);
  const [target, setTarget] = useState(0); // 🎯 admin-set monthly target

  useEffect(() => {
    fetchAllData();
    fetchTarget();
  }, [id]);

  // 🎯 fetch this month's target — own target ("/targets/me"),
  // or another user's target if an admin is viewing via :id
  const fetchTarget = async () => {
    try {
      const endpoint = id ? `/targets/${id}` : "/targets/me";
      const res = await API.get(endpoint);
      setTarget(Number(res.data?.target_amount || 0));
    } catch (err) {
      console.log("Target fetch error:", err);
    }
  };

  const fetchAllData = async () => {
    try {
      const [callRes, followRes, meetRes, dealRes] = await Promise.all([
        API.get("/calllogs"),
        API.get("/followups"),
        API.get(`/meetings?role=${localStorage.getItem("role")}&user_id=${localStorage.getItem("userId")}`),
        API.get("/deals")
      ]);

      const userId = id || localStorage.getItem("userId");

      setCalls(callRes.data.filter(c => c.user_id == userId));
      setFollowups(followRes.data.filter(f => f.user_id == userId));
setMeetings(meetRes.data);
   setDeals(dealRes.data.filter(d => String(d.created_by) === String(userId)));

    } catch (err) {
      console.log(err);
    }
  };

  const totalCalls = calls.length;
  const totalFollowups = followups.length;
  const totalMeetings = meetings.length;
 const totalDeals = deals;   // ✅ array hi rakho

 const totalSales = deals.reduce(
  (sum, d) => sum + Number(d.received_amount || 0),
  0
);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const monthlyData = months.map((month, index) => {
    const monthDeals = deals.filter(d => new Date(d.created_at).getMonth() === index);

    return {
      month,
      sales: monthDeals.reduce((sum, d) => sum + Number(d.received_amount || 0), 0)
    };
  });

  // 🎯 target now comes from the admin-set value (see fetchTarget above),
  // no longer hardcoded.

   const pieData = [
  { name: "Achieved", value: totalSales },
  { name: "Remaining", value: Math.max(target - totalSales, 0) }
];

  return (
    <div className="dashboard-page">

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="top-header"
      >
        Dashboard
      </motion.div>

      {/* KPI */}
      <div className="kpi-grid">

        {[ 
          { title: "Total Calls", value: totalCalls },
          { title: "Followups", value: totalFollowups },
          { title: "Meetings", value: totalMeetings },
          { title: "Total Deals", value: totalDeals.length }
        ].map((item, i) => (

          <motion.div
            key={i}
            className="kpi-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.2 }}
            whileHover={{ scale: 1.05 }}
          >
            <p>{item.title}</p>
            <h2>{item.value}</h2>
          </motion.div>

        ))}

      </div>

      {/* CHARTS */}
      <div className="chart-grid">

        {/* PIE */}
        <motion.div
          className="chart-box"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <h4>Target vs Achieved</h4>

          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                innerRadius={60}
                outerRadius={90}
                isAnimationActive={true}
                animationDuration={1200}
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* BAR */}
        <motion.div
          className="chart-box"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h4>Monthly Sales</h4>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />

              <Bar
                dataKey="sales"
                radius={[8,8,0,0]}
                isAnimationActive={true}
                animationDuration={1500}
              />
            </BarChart>
          </ResponsiveContainer>

        </motion.div>

      </div>

    </div>
  );
}

export default TelesalesDashboard;